// CreateOptionSetModal — serves BOTH create and edit ("Add form set" / "Edit
// question set"…): a Kind card pair, Name + SQL column, and an ordered
// option-value list. Create POSTs /form-sets/create; passing `set` switches to
// edit, seeding from that row and PATCHing /form-sets/update/:id.
//
// Both Name and SQL column are required on create (the design only asterisks
// Name; the confirmed contract needs both), and SQL column follows the same
// identifier rules as the company field's dbColumn — see sqlColumnErrorKey. In
// EDIT mode the update body carries neither `type` nor `sqlColumn`, and the
// design hides both outright (pencil icon, Name full-width, Options only) —
// so the kind cards and the SQL field simply don't render, and neither is
// validated or sent. Options need at least one non-empty value; blank rows are
// dropped and positions re-numbered 1-based from the surviving row order. Edit
// payload rules (confirmed): existing options keep their backend id as
// `formSetOptionId`, options added during the edit OMIT that key entirely, and
// every existing option missing from the surviving rows (trashed, or blanked
// and therefore dropped) is reported in `deletedOptions`.
//
// The draft re-seeds on every open, so Cancel really discards; required-field
// validation runs before the request (same `submitted` idiom as
// CreateCompanyFieldModal).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sqlIdentifierError, { SQL_IDENTIFIER_MAX } from '@/utils/sqlIdentifier';
import toast from 'react-hot-toast';
import { Button, Field, Icon, IconButton, Input, Modal } from '@/components/common';
import {
  useCreateOptionSetMutation,
  useUpdateOptionSetMutation,
  type OptionSet,
  type OptionSetKind,
} from '@/api/endpoints/formSetsApi';
import { KIND_ICONS } from './optionSetColumns';

interface CreateOptionSetModalProps {
  open: boolean;
  onClose: () => void;
  /** Present → edit that set instead of creating a new one. */
  set?: OptionSet | null;
}

// One editor row. `id` is the existing option's backend id — absent on rows
// added in this session, which is what decides whether the payload row carries
// `formSetOptionId`.
interface OptionRow {
  id?: string;
  value: string;
}

// The shared lowercase-identifier rule (utils/sqlIdentifier), mapped to this
// module's own wording.
/** i18n key of the first rule the value breaks, or null when it's valid. */
const sqlColumnErrorKey = (value: string): string | null => {
  const rule = sqlIdentifierError(value);
  return rule ? `data_structure_os_sql_column_${rule}` : null;
};

const KINDS: OptionSetKind[] = ['form_set', 'question_set'];

const CreateOptionSetModal = ({ open, onClose, set = null }: CreateOptionSetModalProps) => {
  const { t } = useTranslation();
  const [createOptionSet, { isLoading: creating }] = useCreateOptionSetMutation();
  const [updateOptionSet, { isLoading: updating }] = useUpdateOptionSetMutation();
  const saving = creating || updating;
  const isEdit = Boolean(set);
  const [kind, setKind] = useState<OptionSetKind>('form_set');
  const [title, setTitle] = useState('');
  const [sqlColumn, setSqlColumn] = useState('');
  const [rows, setRows] = useState<OptionRow[]>([{ value: '' }]);
  const [submitted, setSubmitted] = useState(false);

  // Re-seed each time the modal opens — from the edited row (options already
  // position-sorted by the normalizer), or blank for a new set. Discards
  // leftovers on close either way.
  useEffect(() => {
    if (!open) return;
    setKind(set?.kind ?? 'form_set');
    setTitle(set?.title ?? '');
    setSqlColumn(set?.sqlColumn ?? '');
    setRows(
      set?.options.length
        ? set.options.map((o) => ({ id: o.id, value: o.value }))
        : [{ value: '' }],
    );
    setSubmitted(false);
  }, [open, set]);

  const titleMissing = !title.trim();
  // Skipped in edit mode: the input is disabled and the value can't change.
  const sqlErrorKey = isEdit ? null : sqlColumnErrorKey(sqlColumn);
  const filledRows = rows.map((r) => ({ ...r, value: r.value.trim() })).filter((r) => r.value);
  const noOptions = filledRows.length === 0;
  const invalid = titleMissing || Boolean(sqlErrorKey) || noOptions;

  const patchRow = (i: number, value: string) =>
    setRows((prev) => prev.map((r, x) => (x === i ? { ...r, value } : r)));

  const isForm = kind === 'form_set';

  const save = async () => {
    // Validate before any backend call: surface errors, bail while invalid.
    setSubmitted(true);
    if (invalid) return;
    const name = title.trim();
    try {
      if (set) {
        // Every existing option missing from the surviving rows was deleted —
        // whether its row was trashed or just blanked out.
        const keptIds = new Set(filledRows.map((r) => r.id).filter(Boolean));
        await updateOptionSet({
          id: set.id,
          body: {
            title: name,
            options: filledRows.map((r, i) => ({
              // New rows OMIT the key entirely (confirmed contract).
              ...(r.id ? { formSetOptionId: r.id } : {}),
              optionValue: r.value,
              position: i + 1,
            })),
            deletedOptions: set.options.filter((o) => !keptIds.has(o.id)).map((o) => o.id),
          },
        }).unwrap();
        toast.success(t('data_structure_os_updated', { name }));
      } else {
        await createOptionSet({
          type: kind,
          title: name,
          sqlColumn: sqlColumn.trim(),
          options: filledRows.map((r, i) => ({ optionValue: r.value, position: i + 1 })),
        }).unwrap();
        toast.success(t('data_structure_os_created', { name }));
      }
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const createTitleKey = isForm
    ? 'data_structure_os_create_title_form'
    : 'data_structure_os_create_title_question';
  const editTitleKey = isForm
    ? 'data_structure_os_edit_title_form'
    : 'data_structure_os_edit_title_question';

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={isEdit ? 'pencil' : KIND_ICONS[kind]}
      title={t(isEdit ? editTitleKey : createTitleKey)}
      subtitle={t('data_structure_os_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={isEdit ? 'check' : 'plus'} onClick={save} loading={saving}>
            {t(
              isEdit
                ? 'button_save_changes'
                : isForm
                  ? 'data_structure_os_create_form'
                  : 'data_structure_os_create_question',
            )}
          </Button>
        </>
      }
    >
      <div className='option-set-create'>
        {/* Kind is fixed once created (the update body has no `type`), and the
            edit design drops the picker entirely rather than disabling it. */}
        {!isEdit && (
          <fieldset className='option-set-create__kind' role='radiogroup'>
            <legend className='option-set-create__kind-label'>
              {t('data_structure_os_kind_label')}
            </legend>
            <div className='option-set-create__kind-cards'>
              {KINDS.map((k) => (
                <label
                  key={k}
                  className={`option-set-create__kind-card${kind === k ? ' is-selected' : ''}`}
                >
                  <input
                    type='radio'
                    name='option-set-kind'
                    className='option-set-create__kind-input'
                    checked={kind === k}
                    onChange={() => setKind(k)}
                  />
                  <span className='option-set-create__kind-icon' aria-hidden='true'>
                    <Icon name={KIND_ICONS[k]} size={17} />
                  </span>
                  {t(`data_structure_os_kind_${k}`)}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {/* Edit hides SQL column too (not in the update body), so Name goes
            full-width there instead of sharing the two-column row. */}
        <div className={isEdit ? undefined : 'option-set-create__row'}>
          <Field
            label={t('data_structure_os_name')}
            required
            error={submitted && titleMissing ? t('data_structure_os_name_required') : undefined}
          >
            <Input
              autoFocus
              value={title}
              placeholder={t('data_structure_os_name_ph')}
              error={submitted && titleMissing}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          {!isEdit && (
            <Field
              label={t('data_structure_os_sql_column')}
              required
              error={
                submitted && sqlErrorKey ? t(sqlErrorKey, { max: SQL_IDENTIFIER_MAX }) : undefined
              }
            >
              <Input
                value={sqlColumn}
                placeholder={t('data_structure_os_sql_column_ph')}
                maxLength={SQL_IDENTIFIER_MAX}
                error={submitted && Boolean(sqlErrorKey)}
                onChange={(e) => setSqlColumn(e.target.value)}
              />
            </Field>
          )}
        </div>

        <div className='option-set-create__options'>
          <div className='option-set-create__options-head'>
            <span className='option-set-create__options-title'>
              {t('data_structure_os_options_label')}
            </span>
            <span className='option-set-create__options-count'>
              {t('data_structure_os_options_added', { count: filledRows.length })}
            </span>
          </div>

          {rows.map((row, i) => (
            // Index keys are fine — rows only append/remove in place.
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className='option-set-create__option-row'>
              <span className='option-set-create__option-index' aria-hidden='true'>
                {i + 1}
              </span>
              <Input
                size='sm'
                value={row.value}
                placeholder={t('data_structure_os_option_value_ph')}
                aria-label={t('data_structure_os_option_value_ph')}
                onChange={(e) => patchRow(i, e.target.value)}
              />
              <IconButton
                icon='trash-2'
                variant='ghost'
                size='sm'
                className='option-set-create__option-delete'
                aria-label={t('data_structure_os_option_delete')}
                disabled={rows.length === 1}
                onClick={() => setRows((prev) => prev.filter((_, x) => x !== i))}
              />
            </div>
          ))}

          {submitted && noOptions && (
            <span className='option-set-create__error' role='alert'>
              <Icon name='circle-alert' size={15} aria-hidden='true' />
              {t('data_structure_os_options_min_one')}
            </span>
          )}

          <button
            type='button'
            className='option-set-create__add'
            onClick={() => setRows((prev) => [...prev, { value: '' }])}
          >
            <Icon name='plus' size={15} />
            {t('data_structure_os_add_option')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateOptionSetModal;
